import React, { useState, useEffect } from 'react';
import { SerialOptions, BaudRate } from '../../types/serial';
import { X } from 'lucide-react';

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConnect: (options: SerialOptions, isTestMode: boolean) => void;
    isTestMode?: boolean;
    onTestModeChange?: (value: boolean) => void;
}

const MinimalSettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, onConnect, isTestMode: propIsTestMode, onTestModeChange }) => {
    const [options, setOptions] = useState<SerialOptions>({
        baudRate: 115200,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none'
    });
    const [isTestMode, setIsTestMode] = useState(propIsTestMode || false);

    useEffect(() => {
        if (propIsTestMode !== undefined) {
            setIsTestMode(propIsTestMode);
        }
    }, [propIsTestMode]);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onConnect(options, isTestMode);
        onClose();
    };

    const selectClass = "w-full border-b border-black bg-transparent py-1 font-mono text-sm outline-none rounded-none";
    const labelClass = "block text-xs font-bold uppercase tracking-wide mb-1 text-gray-500";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm p-4">
            <div className="w-full max-w-sm border border-black bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-6">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-sm font-bold uppercase tracking-widest">Connection Settings</h2>
                    <button onClick={onClose} className="hover:text-gray-500 transition">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className={labelClass}>Baud Rate</label>
                        <select
                            value={options.baudRate}
                            onChange={(e) => setOptions({ ...options, baudRate: Number(e.target.value) })}
                            className={selectClass}
                        >
                            <option value={BaudRate.B9600}>9600</option>
                            <option value={BaudRate.B19200}>19200</option>
                            <option value={BaudRate.B38400}>38400</option>
                            <option value={BaudRate.B57600}>57600</option>
                            <option value={BaudRate.B115200}>115200</option>
                            <option value={BaudRate.B230400}>230400</option>
                            <option value={BaudRate.B921600}>921600</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Data Bits</label>
                            <select
                                value={options.dataBits}
                                onChange={(e) => setOptions({ ...options, dataBits: Number(e.target.value) as 7 | 8 })}
                                className={selectClass}
                            >
                                <option value={8}>8</option>
                                <option value={7}>7</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Stop Bits</label>
                            <select
                                value={options.stopBits}
                                onChange={(e) => setOptions({ ...options, stopBits: Number(e.target.value) as 1 | 2 })}
                                className={selectClass}
                            >
                                <option value={1}>1</option>
                                <option value={2}>2</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Parity</label>
                            <select
                                value={options.parity}
                                onChange={(e) => setOptions({ ...options, parity: e.target.value as any })}
                                className={selectClass}
                            >
                                <option value="none">None</option>
                                <option value="even">Even</option>
                                <option value="odd">Odd</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Flow Control</label>
                            <select
                                value={options.flowControl}
                                onChange={(e) => setOptions({ ...options, flowControl: e.target.value as any })}
                                className={selectClass}
                            >
                                <option value="none">None</option>
                                <option value="hardware">Hardware</option>
                            </select>
                        </div>
                    </div>

                    <div className="pt-2 space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={isTestMode}
                                onChange={(e) => {
                                    setIsTestMode(e.target.checked);
                                    onTestModeChange?.(e.target.checked);
                                }}
                                className="w-4 h-4 border border-black rounded-none checked:bg-black focus:ring-0"
                            />
                            <div>
                                <div className="text-sm font-bold uppercase">Test Mode</div>
                                <div className="text-xs text-gray-500">Emulate connection</div>
                            </div>
                        </label>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-black text-white hover:bg-gray-800 font-bold uppercase tracking-wider py-3 text-xs transition-colors"
                    >
                        {isTestMode ? 'Connect (Test)' : 'Save & Connect'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default MinimalSettingsModal;
